import apiService from './api';
import type { ApiResponse } from '../types';
import { openDocument } from '../utils/openDocument';

export type CredentialStatus = 'valid' | 'expiring_soon' | 'expired' | 'missing';

export interface SprayCredentialType {
  id: number;
  organisation_id: number;
  name: string;
  description: string | null;
  is_required_for_operators: boolean;
  is_active: boolean;
  created_at: string;
  created_by: number | null;
}

export interface UserSprayCredential {
  id: number;
  user_id: number;
  organisation_id: number;
  credential_type_id: number;
  credential_type_name: string;
  is_required_for_operators: boolean;
  credential_number: string | null;
  issuer: string | null;
  issued_date: string | null;
  expiry_date: string | null;
  document_original_name: string | null;
  document_mime_type: string | null;
  has_document: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
  status: Exclude<CredentialStatus, 'missing'>;
}

export interface OperatorReadinessRow {
  user_id: number;
  username: string;
  first_name: string | null;
  last_name: string | null;
  license_type: 'operator' | 'creator';
  credential_type_id: number;
  credential_type_name: string;
  is_required_for_operators: boolean;
  credential_id: number | null;
  credential_number: string | null;
  issuer: string | null;
  issued_date: string | null;
  expiry_date: string | null;
  document_original_name: string | null;
  has_document: boolean;
  status: CredentialStatus;
}

export interface CredentialGaps {
  missing: Array<{ id: number; name: string }>;
  expired: Array<{ id: number; credential_type_id: number; credential_type_name: string; expiry_date: string | null }>;
}

// ---------------------------------------------------------------------------
// Credential types (org catalog)
// ---------------------------------------------------------------------------

export async function listCredentialTypes(includeInactive = false): Promise<ApiResponse<SprayCredentialType[]>> {
  const q = includeInactive ? '?include_inactive=true' : '';
  return apiService.get<SprayCredentialType[]>(`/spray/credential-types${q}`);
}

export async function createCredentialType(data: {
  name: string;
  description?: string | null;
  is_required_for_operators?: boolean;
}): Promise<ApiResponse<SprayCredentialType>> {
  return apiService.post<SprayCredentialType>('/spray/credential-types', data);
}

export async function updateCredentialType(
  id: number,
  data: Partial<Pick<SprayCredentialType, 'name' | 'description' | 'is_required_for_operators' | 'is_active'>>
): Promise<ApiResponse<SprayCredentialType>> {
  return apiService.put<SprayCredentialType>(`/spray/credential-types/${id}`, data);
}

export async function deactivateCredentialType(id: number): Promise<ApiResponse<unknown>> {
  return apiService.delete<unknown>(`/spray/credential-types/${id}`);
}

// ---------------------------------------------------------------------------
// User credentials
// ---------------------------------------------------------------------------

export async function listMyCredentials(): Promise<ApiResponse<UserSprayCredential[]>> {
  return apiService.get<UserSprayCredential[]>('/spray/credentials/me');
}

export async function listUserCredentials(userId: number): Promise<ApiResponse<UserSprayCredential[]>> {
  return apiService.get<UserSprayCredential[]>(`/spray/credentials/user/${userId}`);
}

export async function getReadinessReport(): Promise<ApiResponse<OperatorReadinessRow[]>> {
  return apiService.get<OperatorReadinessRow[]>('/spray/credentials/report');
}

/**
 * Create a credential. Uses FormData so an optional `document` file can be
 * attached in the same request as the metadata.
 */
export async function createCredential(args: {
  credential_type_id: number;
  credential_number?: string;
  issuer?: string;
  issued_date?: string;
  expiry_date?: string;
  notes?: string;
  document?: File | null;
  user_id?: number; // manager acting on behalf of another user
}): Promise<ApiResponse<UserSprayCredential>> {
  const form = new FormData();
  form.append('credential_type_id', String(args.credential_type_id));
  if (args.credential_number) form.append('credential_number', args.credential_number);
  if (args.issuer) form.append('issuer', args.issuer);
  if (args.issued_date) form.append('issued_date', args.issued_date);
  if (args.expiry_date) form.append('expiry_date', args.expiry_date);
  if (args.notes) form.append('notes', args.notes);
  if (args.user_id) form.append('user_id', String(args.user_id));
  if (args.document) form.append('document', args.document);
  // Axios sets the multipart boundary automatically when given FormData.
  return apiService.post<UserSprayCredential>('/spray/credentials', form);
}

export async function updateCredential(
  id: number,
  args: {
    credential_number?: string;
    issuer?: string;
    issued_date?: string;
    expiry_date?: string;
    notes?: string;
    document?: File | null;
  }
): Promise<ApiResponse<UserSprayCredential>> {
  const form = new FormData();
  if (args.credential_number !== undefined) form.append('credential_number', args.credential_number);
  if (args.issuer !== undefined) form.append('issuer', args.issuer);
  if (args.issued_date !== undefined) form.append('issued_date', args.issued_date);
  if (args.expiry_date !== undefined) form.append('expiry_date', args.expiry_date);
  if (args.notes !== undefined) form.append('notes', args.notes);
  if (args.document) form.append('document', args.document);
  return apiService.put<UserSprayCredential>(`/spray/credentials/${id}`, form);
}

export async function deleteCredential(id: number): Promise<ApiResponse<unknown>> {
  return apiService.delete<unknown>(`/spray/credentials/${id}`);
}

/** A credential's attached document. The route needs the bearer token, so it cannot be a plain href. */
export async function openCredentialDocument(c: Pick<UserSprayCredential, 'id' | 'document_original_name'>): Promise<void> {
  await openDocument(`/spray/credentials/${c.id}/document`, c.document_original_name || `credential-${c.id}`);
}
